import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ApiService from '../services/apiService';
import { useUser } from '../contexts/UserContext';

const ProfilePage = () => {
  const { user } = useUser();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    headline: '',
    location: '',
    next_review_date: '',
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await ApiService.getProfile();
        const p = res?.data || res;
        setProfile(p);
        setFormData({
          name: p?.name || '',
          headline: p?.headline || '',
          location: p?.location || '',
          next_review_date: p?.next_review_date ? p.next_review_date.split('T')[0] : '',
        });
      } catch {
        // profile may not exist yet — that's ok
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) loadProfile();
  }, [user?.id]);

  const handleEdit = () => {
    setFormData({
      name: profile?.name || '',
      headline: profile?.headline || '',
      location: profile?.location || '',
      next_review_date: profile?.next_review_date ? profile.next_review_date.split('T')[0] : '',
    });
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await ApiService.updateProfile(formData);
      const updated = res?.data || res;
      setProfile(updated);
      setIsEditing(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = () => {
    if (!profile?.public_profile_slug) return;
    navigator.clipboard.writeText(`${window.location.origin}/u/${profile.public_profile_slug}`);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-xl mx-auto px-4 py-8 space-y-4">

        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-gray-900">Profile</h1>
          <p className="text-sm text-gray-500 mt-0.5">Your name and headline appear on your public profile.</p>
        </div>

        {/* Public profile link */}
        {profile?.public_profile_slug ? (
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-400 mb-0.5">Your public profile</p>
              <Link
                to={`/u/${profile.public_profile_slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-800 hover:text-gray-900 font-medium truncate block"
              >
                verfolio.io/u/{profile.public_profile_slug}
              </Link>
            </div>
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
            >
              {copiedLink ? (
                <span className="text-green-700">Copied!</span>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy link
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3">
            <p className="text-xs text-amber-700">
              <span className="font-semibold">No public URL yet.</span> Save your profile below to generate your shareable link.
            </p>
          </div>
        )}

        {/* Profile card */}
        <div className="bg-white border border-gray-200 rounded-xl">
          {isEditing ? (
            <form onSubmit={handleSave} className="p-5 space-y-4">
              <h2 className="text-sm font-semibold text-gray-900">Edit profile</h2>

              {saveError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg">{saveError}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Saad Idris"
                  maxLength={255}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400"
                  disabled={saving}
                  required
                />
                <p className="mt-1 text-xs text-gray-400">Used to generate your profile URL — can't be changed later.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Headline</label>
                <input
                  type="text"
                  value={formData.headline}
                  onChange={e => setFormData(p => ({ ...p, headline: e.target.value }))}
                  placeholder="e.g. Staff Engineer · TestAssure"
                  maxLength={255}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400"
                  disabled={saving}
                />
                <p className="mt-1 text-xs text-gray-400">Shown below your name on your public profile.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={e => setFormData(p => ({ ...p, location: e.target.value }))}
                  placeholder="e.g. London, UK"
                  maxLength={255}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400"
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Next performance review</label>
                <input
                  type="date"
                  value={formData.next_review_date}
                  onChange={e => setFormData(p => ({ ...p, next_review_date: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400"
                  disabled={saving}
                />
                <p className="mt-1 text-xs text-gray-400">We'll show a countdown on your achievements page so your record is ready in time.</p>
              </div>

              <div className="flex justify-end gap-3 pt-1 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                  disabled={saving || !formData.name.trim()}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          ) : (
            <div className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-base font-semibold text-gray-900">{profile?.name || user?.email}</p>
                  {profile?.headline && <p className="text-sm text-gray-500 mt-0.5">{profile.headline}</p>}
                  {profile?.location && <p className="text-xs text-gray-400 mt-1">{profile.location}</p>}
                  {profile?.next_review_date && (
                    <p className="text-xs text-gray-400 mt-1">
                      Next review: {new Date(profile.next_review_date + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                  {!profile?.name && (
                    <p className="text-xs text-gray-400 mt-1">No name set yet — click Edit to add one.</p>
                  )}
                </div>
                <button
                  onClick={handleEdit}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Links to other sections */}
        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
          <Link
            to="/positions"
            className="flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-gray-700">Work history</p>
                <p className="text-xs text-gray-400">Manage your positions</p>
              </div>
            </div>
            <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
          <Link
            to="/achievements"
            className="flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-gray-700">Achievements</p>
                <p className="text-xs text-gray-400">Capture wins and request verification</p>
              </div>
            </div>
            <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

      </div>
    </div>
  );
};

export default ProfilePage;
